import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth, hasFullAccess } from './AuthContext';
import SubscribeModal from '../components/SubscribeModal';

const SubscriptionGateContext = createContext(null);

/** Only these targets open subscribe — everything else stays browsable */
const PAYWALL_SELECTOR = [
  '.art-story-card:not([data-sub-free])',
  '.art-featured-card:not([data-sub-free])',
  '[data-requires-sub]',
  '.paper-cta:not(.paper-preview-btn)',
  '.ytc-preview',
  '.ytc-pl-item',
  '.ytc-short-card',
].join(', ');

export function SubscriptionGateProvider({ children }) {
  const { user } = useAuth();
  const access = hasFullAccess(user);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [reviewCheckout] = useState(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('checkout') === '1'
  );

  const openSubscribe = useCallback(() => {
    if (!user) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?next=${next}`;
      return;
    }
    setSubscribeOpen(true);
  }, [user]);
  const closeSubscribe = useCallback(() => setSubscribeOpen(false), []);

  useEffect(() => {
    if (user && reviewCheckout) setSubscribeOpen(true);
  }, [user, reviewCheckout]);

  useEffect(() => {
    if (access && !reviewCheckout) setSubscribeOpen(false);
  }, [access, reviewCheckout]);

  useEffect(() => {
    if (access) return undefined;

    const root = document.querySelector('.home-page');
    if (!root) return undefined;

    const onCapture = (e) => {
      const target = e.target.closest(PAYWALL_SELECTOR);
      if (!target) return;
      if (target.closest('.sub-modal')) return;
      if (target.hasAttribute('data-sub-free')) return;

      e.preventDefault();
      e.stopPropagation();
      if (!user) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/login?next=${next}`;
        return;
      }
      setSubscribeOpen(true);
    };

    root.addEventListener('click', onCapture, true);
    return () => root.removeEventListener('click', onCapture, true);
  }, [access, user]);

  return (
    <SubscriptionGateContext.Provider value={{ hasFullAccess: access, openSubscribe, closeSubscribe }}>
      {children}
      {subscribeOpen && (!access || reviewCheckout) && <SubscribeModal onClose={closeSubscribe} />}
    </SubscriptionGateContext.Provider>
  );
}

export function useSubscriptionGate() {
  const ctx = useContext(SubscriptionGateContext);
  if (!ctx) throw new Error('useSubscriptionGate must be used within SubscriptionGateProvider');
  return ctx;
}
